"use client";

/**
 * Step 2: Date and Time Selection
 * Native date picker with available time slots
 */

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { apiFetch } from "@/lib/api";
import type { TimeSlot } from "@/types/booking-v2";
import { Clock, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

interface DateTimeStepProps {
  tenantSlug: string;
  selectedDate: Date | null;
  selectedTime: string | null; // Display label (e.g., "09:00")
  totalDuration: number;
  onDateChange: (date: Date | null) => void;
  onTimeChange: (timeLabel: string | null, timeISO: string | null) => void;
}

export function DateTimeStep({
  tenantSlug,
  selectedDate,
  selectedTime,
  totalDuration,
  onDateChange,
  onTimeChange,
}: DateTimeStepProps) {
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch available slots when date changes
  useEffect(() => {
    if (!selectedDate) {
      setAvailableSlots([]);
      return;
    }

    const fetchSlots = async () => {
      setIsLoadingSlots(true);
      setError(null);
      try {
        const dateStr = selectedDate.toISOString().split("T")[0];
        const slots = await apiFetch<TimeSlot[]>(
          `/public/${tenantSlug}/available-slots?date=${dateStr}&duration=${totalDuration}`
        );
        setAvailableSlots(slots);
      } catch (err) {
        console.error("Error fetching slots:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível carregar os horários"
        );
        setAvailableSlots([]);
      } finally {
        setIsLoadingSlots(false);
      }
    };

    fetchSlots();
  }, [selectedDate, tenantSlug, totalDuration]);

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  // Filter only available slots and group by period
  const groupSlotsByPeriod = (slots: TimeSlot[]) => {
    const morning: TimeSlot[] = [];
    const afternoon: TimeSlot[] = [];
    const evening: TimeSlot[] = [];

    // Only include available slots
    slots
      .filter((slot) => slot.available)
      .forEach((slot) => {
        const hour = parseInt(slot.time.split(":")[0]);
        if (hour < 12) morning.push(slot);
        else if (hour < 18) afternoon.push(slot);
        else evening.push(slot);
      });

    return { morning, afternoon, evening };
  };

  const { morning, afternoon, evening } = groupSlotsByPeriod(availableSlots);

  return (
    <div className="space-y-4 h-full flex flex-col">
      {/* Compact duration info */}
      <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3 shrink-0">
        <p className="text-sm text-blue-900 dark:text-blue-100 flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span>
            Duração total: <strong>{formatDuration(totalDuration)}</strong>
          </span>
        </p>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto -mx-4 md:mx-0 px-4 md:px-0 overscroll-contain">
        <div className="space-y-6 pb-4">
          {/* Date Picker */}
          <div className="space-y-2">
            <h3 className="font-semibold text-base">Selecione a Data</h3>
            <DatePicker
              date={selectedDate ?? undefined}
              onDateChange={(date) => {
                onDateChange(date as Date);
                onTimeChange(null, null); // Reset time when date changes
              }}
              placeholder="Selecione uma data"
              closeOnSelect={true}
              disablePastDates={true}
              className="h-14 text-base"
            />
          </div>{" "}
          {/* Time slots */}
          {selectedDate && (
            <div className="space-y-4">
              <h3 className="font-semibold text-base">Horários Disponíveis</h3>

              {isLoadingSlots && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              )}

              {error && (
                <div className="text-center py-8 text-destructive">{error}</div>
              )}

              {!isLoadingSlots &&
                !error &&
                morning.length === 0 &&
                afternoon.length === 0 &&
                evening.length === 0 && (
                  <div className="text-center py-8 px-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
                      <Clock className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground mb-2">
                      Não há horários disponíveis para esta data
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Tente selecionar outra data
                    </p>
                  </div>
                )}

              {!isLoadingSlots &&
                !error &&
                (morning.length > 0 ||
                  afternoon.length > 0 ||
                  evening.length > 0) && (
                  <div className="space-y-6">
                    {/* Morning */}
                    {morning.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <Badge variant="outline" className="text-sm">
                            🌅 Manhã
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {morning.length} horário
                            {morning.length > 1 ? "s" : ""} disponível
                            {morning.length > 1 ? "is" : ""}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                          {morning.map((slot) => (
                            <Button
                              key={slot.time}
                              variant={
                                selectedTime === slot.time
                                  ? "default"
                                  : "outline"
                              }
                              onClick={() => onTimeChange(slot.time, slot.startAt)}
                              className="h-12 text-base font-medium"
                            >
                              {slot.time}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Afternoon */}
                    {afternoon.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <Badge variant="outline" className="text-sm">
                            ☀️ Tarde
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {afternoon.length} horário
                            {afternoon.length > 1 ? "s" : ""} disponível
                            {afternoon.length > 1 ? "is" : ""}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                          {afternoon.map((slot) => (
                            <Button
                              key={slot.time}
                              variant={
                                selectedTime === slot.time
                                  ? "default"
                                  : "outline"
                              }
                              onClick={() => onTimeChange(slot.time, slot.startAt)}
                              className="h-12 text-base font-medium"
                            >
                              {slot.time}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Evening */}
                    {evening.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <Badge variant="outline" className="text-sm">
                            🌙 Noite
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {evening.length} horário
                            {evening.length > 1 ? "s" : ""} disponível
                            {evening.length > 1 ? "is" : ""}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                          {evening.map((slot) => (
                            <Button
                              key={slot.time}
                              variant={
                                selectedTime === slot.time
                                  ? "default"
                                  : "outline"
                              }
                              onClick={() => onTimeChange(slot.time, slot.startAt)}
                              className="h-12 text-base font-medium"
                            >
                              {slot.time}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
